import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { hasNativePlugin } from '../NativeBiometricAuthService'

// Native (Capacitor) push helper. Everything here is only meaningful when the
// app runs inside the native shell; on web these functions are no-ops so the
// existing Firebase Web Messaging path stays untouched.

// Requires the plugin itself, not just a native platform: older shells run this
// same bundle without FirebaseMessaging compiled in, and calling a plugin that is
// not there is what hung the app on the login screen (see nativePlugins.js).
export const isNativePlatform = () => Capacitor.isNativePlatform() &&
  hasNativePlugin('FirebaseMessaging')

// Requests notification permission and returns a native FCM registration token
// (or '' when unavailable/denied). The token is a normal FCM token, so the
// existing backend can send to it without any change.
export const getNativeToken = async () => {
  try {
    const permission = await FirebaseMessaging.requestPermissions()
    if (permission?.receive !== 'granted') return ''
    const { token } = await FirebaseMessaging.getToken()
    return token || ''
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to obtain native push token', error)
    return ''
  }
}

// Subscribes to FCM token rotation. Native tokens change over time; when they
// do we must re-register the new token with the backend.
export const addNativeTokenRefreshListener = async (onToken) => {
  try {
    return FirebaseMessaging.addListener('tokenReceived', (event) => {
      if (event?.token) onToken(event.token)
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to register native token refresh listener', error)
    return null
  }
}

// Foreground + tap listeners. iOS does not show a banner while the app is open,
// so we surface an in-app snackbar via onForeground, and handle taps via onTap.
export const addNativeMessageListeners = async ({ onForeground, onTap }) => {
  const handles = []
  try {
    if (onForeground) {
      handles.push(await FirebaseMessaging.addListener('notificationReceived', (event) => {
        onForeground(event?.notification)
      }))
    }
    if (onTap) {
      handles.push(await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        onTap(event?.notification)
      }))
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to register native message listeners', error)
  }
  return () => handles.forEach((handle) => handle?.remove?.())
}
